/**
 * <SEO /> — Componente per gestione dinamica meta tag, Open Graph, Twitter Card e JSON-LD
 * per ogni pagina pubblica del sito web.
 *
 * Props:
 *  - title: string (max ~60 caratteri, viene appeso " | Energy Multiservizi" se non già presente)
 *  - description: string (max ~160 caratteri)
 *  - path: string (path relativo, es. "/servizi/luce-gas") usato per canonical/og:url
 *  - image: string (URL assoluto o relativo) — default og-image.png
 *  - type: "website" | "article" — default "website"
 *  - keywords: string opzionale
 *  - jsonLd: oggetto o array di oggetti per Schema.org JSON-LD
 *  - noindex: bool — se true, blocca indicizzazione (per pagine private)
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://energy-multiservizi.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const SITE_NAME = 'Energy Multiservizi';

const ensureSuffix = (title) => {
  if (!title) return SITE_NAME;
  if (title.toLowerCase().includes('energy multiservizi')) return title;
  const suffix = ` | ${SITE_NAME}`;
  const remaining = 60 - suffix.length;
  return `${title.slice(0, remaining)}${suffix}`;
};

const absoluteUrl = (path) => {
  if (!path) return SITE_URL + '/';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : '/' + path}`;
};

const SEO = ({
  title,
  description,
  path,
  image,
  type = 'website',
  keywords,
  jsonLd,
  noindex = false,
}) => {
  const fullTitle = ensureSuffix(title);
  const desc = (description || '').slice(0, 160);
  const canonical = absoluteUrl(path);
  const ogImage = image
    ? (image.startsWith('http') ? image : absoluteUrl(image))
    : DEFAULT_IMAGE;

  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);

  return (
    <Helmet prioritizeSeoTags>
      {/* Title + description */}
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Robots */}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'} />

      {/* Canonical */}
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="it_IT" />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {jsonLdArray.map((obj) => {
        // Stable key from @type + name when available (es. "Service:Luce e Gas"). Fallback to type.
        const key = `${obj?.['@type'] || 'jsonld'}-${obj?.name || obj?.url || JSON.stringify(obj).length}`;
        return (
          <script key={key} type="application/ld+json">{JSON.stringify(obj)}</script>
        );
      })}
    </Helmet>
  );
};

// Helper per costruire JSON-LD Service standard
export const buildServiceSchema = ({ name, description, path, serviceType }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  description,
  serviceType: serviceType || name,
  url: absoluteUrl(path),
  provider: {
    '@type': 'LocalBusiness',
    name: SITE_NAME,
    url: SITE_URL,
    telephone: '+39 0775 1470039',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Piazza XXV Luglio 35/39',
      addressLocality: 'Ceccano',
      addressRegion: 'FR',
      postalCode: '03023',
      addressCountry: 'IT',
    },
  },
  areaServed: { '@type': 'Country', name: 'Italia' },
});

export default SEO;
